// A content block as a learner sees it, wherever it sits: in a lesson, in a
// homework brief, beside a practice.
//
// Only a lesson's own video blocks count towards the lesson. Anywhere else
// (`trackProgress` false) a video plays freely and reports nothing, so a
// recording in a homework brief can never hold the next lesson shut.

import { useT, useFormat } from '../i18n'
import learn from '../i18n/learn'
import { RichText } from '../lib/richtext'
import { Icon } from '../lib/ui'
import { VideoPlayer } from '../player/VideoPlayer'

export type MediaProgress = {
  position: number
  frontier: number
  watched_seconds: number
  duration_seconds: number
  percent: number
  completed: boolean
}

export type VideoBlock = {
  video_id: string
  title?: string
  description?: string
  poster_path?: string
  duration_seconds: number
  /** The transcoding state; only a ready video can be played. */
  status?: 'pending' | 'processing' | 'ready' | 'failed'
}
export type AudioBlock = { file_id: string; title?: string; duration_seconds?: number }
export type MaterialFile = { file_id: string; filename: string; size_bytes?: number }

export type Block =
  | { id: string; kind: 'video'; seq: number; progress?: MediaProgress; payload: VideoBlock }
  | { id: string; kind: 'audio'; seq: number; progress?: MediaProgress; payload: AudioBlock }
  | { id: string; kind: 'text'; seq: number; payload: { html: string } }
  | { id: string; kind: 'image'; seq: number; payload: { file_id: string; alt?: string; caption?: string } }
  | { id: string; kind: 'iframe'; seq: number; payload: { url: string; height?: number } }
  | { id: string; kind: 'materials'; seq: number; payload: { files: MaterialFile[] } }

/** The sandbox an embedded page gets: never its own origin's rights when that origin is ours. */
function frameSandbox(url: string): string {
  let ours = true
  try {
    ours = new URL(url, window.location.href).origin === window.location.origin
  } catch {
    ours = true
  }
  // Scripts and the platform's own origin together would let the page reach
  // out of its frame and act as the learner; somebody else's page needs its
  // origin to work (a video player's cookies, its storage) and cannot.
  // i18n-ignore: sandbox tokens
  return ours ? 'allow-scripts allow-presentation allow-popups' : 'allow-scripts allow-same-origin allow-presentation allow-popups'
}

export function BlockView({
  block,
  seekingPrevented = true,
  onCompleted,
  trackProgress = true,
}: {
  block: Block
  /** Whether the platform holds a learner to what they have watched (lesson blocks only). */
  seekingPrevented?: boolean
  onCompleted?: () => void
  /** False outside a lesson's own content: the video reports no progress and is not locked. */
  trackProgress?: boolean
}) {
  const t = useT(learn)
  switch (block.kind) {
    case 'video':
      return (
        <section className="block block--video">
          {block.payload.status && block.payload.status !== 'ready' ? (
            <div className="player player--error" role="status">
              {block.payload.status === 'failed' ? t('videoFailed') : t('videoNotReady')}
            </div>
          ) : trackProgress ? (
            <VideoPlayer
              blockId={block.id}
              videoId={block.payload.video_id}
              initialPosition={block.progress?.position ?? 0}
              initialFrontier={block.progress?.frontier ?? 0}
              preventSeeking={seekingPrevented && !block.progress?.completed}
              poster={block.payload.poster_path}
              onCompleted={onCompleted}
            />
          ) : (
            <VideoPlayer videoId={block.payload.video_id} trackProgress={false} poster={block.payload.poster_path} />
          )}
          {block.payload.description && <p className="block__caption">{block.payload.description}</p>}
        </section>
      )

    case 'text':
      return (
        <section className="block block--text">
          <RichText html={block.payload.html} />
        </section>
      )

    case 'image':
      return (
        <figure className="block block--image">
          <img src={`/files/${block.payload.file_id}`} alt={block.payload.alt ?? ''} loading="lazy" />
          {block.payload.caption && <figcaption>{block.payload.caption}</figcaption>}
        </figure>
      )

    case 'iframe':
      return (
        <section className="block block--iframe">
          <iframe
            src={block.payload.url}
            height={block.payload.height || 480}
            title={t('embeddedMaterial')}
            sandbox={frameSandbox(block.payload.url)}
            // The browser's own default, said out loud: players such as
            // YouTube's refuse to start in a frame that sends no referrer.
            referrerPolicy="strict-origin-when-cross-origin"
            loading="lazy"
            allowFullScreen
          />
        </section>
      )

    case 'materials':
      return (
        <section className="block block--materials">
          <h2>{t('materials')}</h2>
          <ul>
            {block.payload.files.map((file) => (
              <li key={file.file_id}>
                <Icon name="file" size={18} />
                <a href={`/files/${file.file_id}`} download>
                  {file.filename}
                </a>
                {file.size_bytes ? <FileSize bytes={file.size_bytes} /> : null}
              </li>
            ))}
          </ul>
        </section>
      )

    case 'audio':
      return (
        <section className="block block--audio">
          {block.payload.title && <h2>{block.payload.title}</h2>}
          <audio controls src={`/files/${block.payload.file_id}`} aria-label={block.payload.title || t('audioLabel')} />
        </section>
      )
  }
}

/** A file size in the units the reader's language writes them in. */
export function FileSize({ bytes }: { bytes: number }) {
  const t = useT(learn)
  const f = useFormat()
  const text =
    bytes < 1024
      ? t('sizeBytes', { n: f.number(bytes) })
      : bytes < 1024 * 1024
        ? t('sizeKB', { n: f.number(Math.round(bytes / 1024)) })
        : t('sizeMB', { n: f.number(Math.round((bytes / 1024 / 1024) * 10) / 10) })
  return <span className="muted">{text}</span>
}
